import { AdhesionUnit, AdhesionUnitObserverCallback, SystemSlotEvent, SystemSlotEventDetail } from '@mbrtargeting/metatag-shared-types';
import { debounce, getBoundingClientRect, getElementPosition, getScrollPosition, isHTMLElement } from '@mbrtargeting/metatag-utils';
import { ConfigResolver } from '../../loader/essentials/config-resolver.js';
import { Registry } from '../../loader/registry.js';
import { MetaTagAdhesionUnitWatcher } from '../modules/adhesion-unit-watcher/adhesion-unit-watcher.js';
import { LifecycleState } from '../modules/adslot-controller/adslot-lifecycle-state.js';
import { EffectFunction } from './index.js';
import { Dimensions } from '../../interfaces/interfaces.js';

type InlineStyleDeclaration = {
    property: string,
    value: string,
    priority: string, // possible '' | 'important'
}

type StickyStopObserver = { end: () => void, update: () => void }

const EMPTY_STICKY_STOP_OBSERVER: StickyStopObserver = { end: () => undefined, update: () => undefined };

enum State {
    FIXED = 'fixed',
    STOP = 'stop',
    ORIGINAL = 'original',
}

/**
 * save inline styles of given HTMLElement as "original" state
 * used before overwriting these inline styles, so that we can later on recover/reset the original styles.
 * Styles from CSS classes are ignored, since those get re-instated automatically when removing inline properties
 *
 * @param styleList a list of style names we want to backup
 * @param targetNode an html element to extract inline styles from
 * @returns a list of inline style declarations
 */
const extractInlineStylesFromNode = (styleList: string[], targetNode: HTMLElement): InlineStyleDeclaration[] => styleList.map(property => ({
    property,
    value: targetNode.style.getPropertyValue(property),
    priority: targetNode.style.getPropertyPriority(property)
}));

/**
 * set styles on targetNode based on type "InlineStyleDeclaration". If value is empty, property should be deleted from node, otherwise new value and priority are applied
 */
const editInlineStyles = (targetNode: HTMLElement, { property, value, priority }: InlineStyleDeclaration): void => {
    if (value !== '') {
        targetNode.style.setProperty(property, value, priority);
    } else {
        targetNode.style.removeProperty(property);
    }
};

/**
 * todo unsure if all edge cases are covered. Will most likely need some refactoring later on
 * get height of current ad slot, even if some of its  child DOMNodes are not reserving any height in it (are overflowing/absolute and needing space)
 * @param {HTMLElement} element
 * @returns {number}
 */
const getHeightOfHTMLElement = (element: HTMLElement): number => {
    let totalHeight = 0;
    Array.from(element.children).forEach((e) => {
        if (isHTMLElement(e)) totalHeight += e.offsetHeight;
    });
    return totalHeight;
};

/**
 * Will make the slotHull of given ad slot sticky.
 * Sticky will start when user scrolls the site down and either adhesionUnit or upper browser border will start touching hull.
 * Sticky should stop if a "stickyStop" element is present in config, just before the stickyElement will touch it.
 */
export const STICKY_NODE: EffectFunction = (adSlot) => {

    const adhesionUnitWatcher = Registry.lookup<MetaTagAdhesionUnitWatcher>('AdhesionUnitWatcher');
    const config = Registry.lookup<ConfigResolver>('ConfigResolver');

    const states: Record<State, InlineStyleDeclaration[]> = {
        [State.FIXED]: [
            { property: 'top', value: '0px', priority: '' },
            { property: 'position', value: 'fixed', priority: '' },
        ],
        [State.ORIGINAL]: [],
        [State.STOP]: [
            { property: 'position', value: 'absolute', priority: '' },
        ],
    };

    const stickyElement = adSlot.slotHull;
    const containerNode = adSlot.containerNode;

    /**
     * Update the width of the container node to avoid the wrong position of the of sky2(and possibly other sticky slots).
     */
    const editInlineStylesContainerNodeWidth = ({ detail }: CustomEvent<SystemSlotEventDetail>) => {
        if (detail.slot === adSlot.slotName) {
            if(adSlot.dimensions.creative.width > 1) {
                // the right value of the width of the slot can be already in the adSlot.dimensions.creative.width, after reportAdDelivery()
                editInlineStyles(containerNode, { property: 'width', value: adSlot.dimensions.creative.width + 'px', priority: '' });
            } else {
                // if adSlot.dimensions.creative.width is 1, then take the width of containerNode from css.
                const {width: containerNodeWidth} = getCssDimensions(containerNode);
                editInlineStyles(containerNode, { property: 'width', value: containerNodeWidth + 'px', priority: '' });
            }
        }
    }

    const getCssDimensions = (slotElement: HTMLElement): Dimensions => {
        const { width, height } = getBoundingClientRect(slotElement);
        return { width: Math.round(width), height: Math.round(height) };
    };

    window.addEventListener(SystemSlotEvent.SDG_SLOT_CREATIVE_WIDTH_AVAILABLE, editInlineStylesContainerNodeWidth);

    const getStickyStopElement = (): Element | null => {
        return document.querySelector(config.getFeatureSettings().stickyStopUnitQuery);
    };
    //evaluate just-in-time if stickyStop element is present on site and relevant for this adslot. Slow sites might otherwise trip us up.
    const isStickyStopValid = (element: Element | null): element is HTMLElement => {
        return config.getFeatureSettings().stickyStop && isHTMLElement(element);
    };
    /**
     * Add or edit a value for given CSS property. If an empty string as newValue is given, remove the entry from the HTMLElement when styles are applied.
     */
    const updateStateEntry = (updateState: State, cssPropertyName: string, newValue: string, newPriority: string) => {
        const propertyIndex = states[updateState].findIndex(({ property }) => property === cssPropertyName);
        const updateEntry = {
            property: cssPropertyName,
            value: newValue,
            priority: (newPriority) ? newPriority : states[updateState][propertyIndex].priority //update only if given a specific value
        };
        if (propertyIndex > -1) states[updateState].splice(propertyIndex, 1);

        states[updateState].push(updateEntry);

        if (currentState === updateState) editInlineStyles(stickyElement, updateEntry);
    };

    /**
     * activate the given state and apply all styles stored with it.
     */
    const switchToState = (targetState: State) => {
        if (targetState === currentState) return;

        currentState = targetState;
        states[targetState].forEach((entry) => editInlineStyles(stickyElement, entry));
    };

    /**
     * Constructs IntersectionObserver to keep track of "Sticky Stop Element" and return possible "exit". Packaged into "end" function for better readability of effect cleanup.
     *
     * If stickyElement reaches StickyStopElement, change the state to "stop".
     * Since the position of "stickyElement" and the ObserverOptions are informed by the "AdhesionUnit" we need to change the ObserverOptions every time the adhesion unit changes.
     * Therefore, we "delete" the old Observer and create a new one with new options.
     *
     * Options of IntersectionObserver are calculated to make observer use viewport sizes minus stickyNode height as threshold to trigger intersect.
     */
    const startStickyStopObserver = (): StickyStopObserver => {
        let currentObserver: IntersectionObserver;
        const stickyStop = getStickyStopElement();
        if (!isStickyStopValid(stickyStop)) return EMPTY_STICKY_STOP_OBSERVER;

        const intersectionHandler = (entries: IntersectionObserverEntry[]): void => {
            const isIntersecting = entries.some(entry => entry.isIntersecting);
            if (isIntersecting) {
                //intersecting with StickyStop. Cancel stickyEffect and position stickyElement just before stickyStop element.
                const newStickyStopPosition = getElementPosition(stickyStop).top - getHeightOfHTMLElement(stickyElement) - getElementPosition(stickyElement.parentElement!).top;
                updateStateEntry(State.STOP, 'top', newStickyStopPosition + 'px', 'important');
                switchToState(State.STOP);
            } else {
                // If user scrolls from bottom to stickyStop, we do not need to change the state to "fixed" and can keep the state "stop".
                // To detect this, we check if top of the stickyElement has negative value. If yes, we can keep the state "stop".
                if (currentState === State.STOP && stickyElement.getBoundingClientRect().top > 0) switchToState(State.FIXED); //not intersecting anymore, restart stickyEffect
            }
        };
        // we need to debounce intersectionHandler to get right positions after resize, it has also debounced.
        // Important: the debounce time of the intersectionHandler should be at leas 40ms more, than debounce time of the scrollHandler or resizeHandler.
        const debouncedIntersectionHandler = debounce(intersectionHandler, 50);

        /**
         * calculate height & position of current ad slot, and use it to shrink intersection observer.
         * We want to trigger intersection only if the stickyElement is about to touch the stickyStop element
         */
        const setIntersectionObserver = () => {
            // shrink viewport by height of stickyElement, so that intersection observer will trigger when stickyElement is about to touch stickyStop element. Bottom value of the stickyElement should be bigger as 0
            // If bottom value is negative, than use 0 instead.
            const shrinkViewportBottomBy = (Math.round(window.innerHeight - Math.max(stickyElement.getBoundingClientRect().bottom, 0))) * -1;

            currentObserver = new IntersectionObserver(debouncedIntersectionHandler, {
                rootMargin: `0px 0px ${shrinkViewportBottomBy}px 0px`
            });
            currentObserver.observe(stickyStop!);
        };
        setIntersectionObserver(); //start Observer
        return {
            end: () => {
                if (currentObserver) currentObserver.disconnect();
            },
            /**
             * if adhesionUnit is used, changing height of adhesionUnit will change position of stickyElement, needing stickyStopObserver to be set with new options.
             * Kill old observer and reset it with current options.
             */
            update: () => {
                if (currentObserver) {
                    currentObserver.disconnect();
                    setIntersectionObserver();
                }
            }
        };
    };

    /**
     * When user scrolls, check if scroll positions is higher than pre-defined start value. If yes, make sticky.
     * If user scrolls back to top, remove sticky effect as soon pre-defined start value is reached again.
     * To save the resources by recalculating the values for checking states debounce with 5ms is used
     *
     * We aim here for an effect of around 20 frames per second, so its animated enough, but doesn't need to be completely fluid.
     * Recalculate every 50ms should suffice for now.
     *
     * StickyStop is handled by StickyStopObserver, therefore if the state is "stop", don't do anything.
     */
    const scrollHandler = debounce(() => {
        window.requestAnimationFrame(() => {
            // instead of update the necessary values with time-interval, we use debounce and update the values for variable allowSwitchToFixedState self
            parentTop = Math.floor(getElementPosition(stickyElement.parentElement!).top);
            startStickyOver = Math.max(0, parentTop - adhesionUnitHeight);
            scrollTop = getScrollPosition().scrollTop;
            // if scrollTop > startStickyOver,then  in the scrollHandler() state can be changed from original to fixed. If not, then state can be changed from fixed to original.
            allowSwitchToFixedState = scrollTop > startStickyOver;

            // update stickyStopObserver by scrolling only if (top position of sticky Element - adhesionUnitHeight) negative. Otherwise, stickyStopObserver get wrong
            if ((stickyElement.getBoundingClientRect().top - adhesionUnitHeight < 0) && currentState === State.STOP) {
                stickyStopObserver.update();
            }
            if (currentState === State.STOP) return;

            if (currentState === State.ORIGINAL && allowSwitchToFixedState) {
                switchToState(State.FIXED);
                stickyStopObserver = startStickyStopObserver();
            }
            if (currentState === State.FIXED && !allowSwitchToFixedState) {
                switchToState(State.ORIGINAL);
                stickyStopObserver.end();
            }
        });
    }, 5);

    /**
     * debouncedIntersectionHandler should have bigger postpone, than resizeHandler at least 40ms.
     */
    const resizeHandler = debounce(() => {
        stickyStopObserver.update();
        // TODO do we need requestAnimationFrame here?
    }, 5);

    const originalStateCleanUp = () => {
        adhesionUnitWatcher.unobserve(stickyElement);
        stickyStopObserver.end();
        switchToState(State.ORIGINAL);
        window.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', resizeHandler);
        adhesionUnitWatcher.unsubscribe(updateAdhesionUnitHeight);
    };

    /**
     * update top position of stickyNode according to adhesionUnit present on website.
     * When state "fixed" is currently used, will update the css rule also on HTMLElement
     */
    const stickyStopUpdate: AdhesionUnitObserverCallback = (stateChanges) => {
        const { isIntersecting, height } = stateChanges;
        const newHeight = (isIntersecting) ? Math.floor(height) : 0;
        const heightChanged = adhesionUnitHeight !== newHeight;

        if (heightChanged) stickyStopObserver.update();
    };

    let currentState: State = State.ORIGINAL;
    let stickyStopObserver: StickyStopObserver = EMPTY_STICKY_STOP_OBSERVER;
    let adhesionUnitHeight = adhesionUnitWatcher.getAdhesionUnit().height;

    // stickyScrollData
    let parentTop = Math.floor(getElementPosition(stickyElement.parentElement!).top);
    let startStickyOver = Math.max(0, parentTop - adhesionUnitHeight);
    let scrollTop = getScrollPosition().scrollTop;

    // if scrollTop > startStickyOver,then  in the scrollHandler() state can be changed from original to fixed. If not, then state can be changed from fixed to original.
    let allowSwitchToFixedState = scrollTop > startStickyOver;

    // If height or visibility of adhesionUnit was changed, update top values and local variable adhesionUnitHeight
    const updateAdhesionUnitHeight = (adhesionUnit: AdhesionUnit) => {
        adhesionUnitHeight = adhesionUnit.height;
        // Update top value of the original state to avoid side effects, if page has adhesionUnit
        updateStateEntry(State.FIXED, 'top', adhesionUnitHeight + 'px', 'important');
    };

    adhesionUnitWatcher.subscribe(updateAdhesionUnitHeight);

    /**
     * we need the height of creative inside adslot, to accurately calculate stickyStop. So start sticky Effect only after our adslot is filled.
     */
    adSlot.lifecycle.onState(LifecycleState.OCCUPIED, async () => {
        adhesionUnitWatcher.observe(stickyElement, stickyStopUpdate);
        //save inline styles set by publisher or webdev, to reinstate later on effect cleanup
        states[State.ORIGINAL] = extractInlineStylesFromNode(['top', 'left', 'right', 'position', 'transition'], stickyElement);
        scrollHandler();
        window.addEventListener('scroll', scrollHandler);
        window.addEventListener('resize', resizeHandler);
    });

    adSlot.lifecycle.onLeave(LifecycleState.OCCUPIED, async () => originalStateCleanUp());

    return originalStateCleanUp;
};
